import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { PLAN_EDIT_LIMITS } from "../../../model/global.data.library";

import { UserContext } from "../../../model/contexts/user.context";
import { ProjectContext } from "../../../model/contexts/project.context";

import ProjectListFeed from "../ProjectListFeed/ProjectListFeed";
import ProjectModalForm from "../ProjectModalForm/ProjectModalForm";

import { capitalizeFirstLetter } from "../../../controller/global.function.library";

import "./ProjectDashboardContent.scss";

const getEditableProjectLimit = (plan) => {
  return PLAN_EDIT_LIMITS[plan] ?? 1;
};

const ProjectDashboardContent = () => {
  const navigate = useNavigate();

  const { user, userPlan } = useContext(UserContext);
  const {
    userProjects,
    saveUserProject,
    updateSelectedProjectTag,
    countUserProjects,
  } = useContext(ProjectContext);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFormEditMode, setIsFormEditMode] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [projectData, setProjectData] = useState({
    name: "",
    description: "",
    priority: "1",
    status: "Not Started",
    dateCreated: "",
    dateModified: "",
    tag: "",
  });

  const dropdownRef = useRef(null);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [sortOption, setSortOption] = useState("Sort By");

  const [projectTags, setProjectTags] = useState([]);
  const [selectedMenuTag, setSelectedMenuTag] = useState("All");
  const [filteredProjects, setFilteredProjects] = useState([]);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeModalMessage, setUpgradeModalMessage] = useState("");
  const [upgradeModalTitle, setUpgradeModalTitle] = useState("");

  const normalizedUserPlan = (
    userPlan ||
    user?.plan ||
    "freemium"
  ).toLowerCase();
  const editableProjectLimit = getEditableProjectLimit(normalizedUserPlan);

  const isArchivedProject = (project) =>
    (project.tag || "").toLowerCase() === "archived";

  const sortedActiveProjectsForAccess = [...(userProjects || [])]
    .filter((project) => !isArchivedProject(project))
    .sort((a, b) => {
      return (
        new Date(a.createdAt || a.dateCreated || 0) -
        new Date(b.createdAt || b.dateCreated || 0)
      );
    });

  const editableProjectIds = new Set(
    sortedActiveProjectsForAccess
      .slice(0, editableProjectLimit)
      .map((project) => project.id),
  );

  const projectsWithAccess = (userProjects || []).map((project) => {
    const isArchived = isArchivedProject(project);
    const canEdit = !isArchived && editableProjectIds.has(project.id);

    return {
      ...project,
      accessMode: canEdit ? "edit" : "view",
      isOverLimit: !isArchived && !canEdit,
      isArchived,
    };
  });

  const applyTagFilter = (tag, projects = projectsWithAccess) => {
    let nextFilteredProjects = [];

    if (tag === "All") {
      nextFilteredProjects =
        projects.filter(
          (project) => (project.tag || "").toLowerCase() !== "archived",
        ) || [];
    } else if (tag === "Untagged") {
      nextFilteredProjects =
        projects.filter((project) => (project.tag || "").trim() === "") || [];
    } else {
      nextFilteredProjects =
        projects.filter(
          (project) => (project.tag || "").toLowerCase() === tag.toLowerCase(),
        ) || [];
    }

    // console.log("SETTING FILTERED PROJECTS:", nextFilteredProjects);
    setFilteredProjects(nextFilteredProjects);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    createProjectTags();
    applyTagFilter(selectedMenuTag, projectsWithAccess);
  }, [userProjects, selectedMenuTag, editableProjectLimit]);

  const createProjectTags = () => {
    const projects = projectsWithAccess;
    const uniqueProjectTags = new Set();
    const tagList = ["All"];

    for (const project of projects) {
      const rawTag = (project.tag || "").trim();

      if (!rawTag) {
        if (!uniqueProjectTags.has("Untagged")) {
          uniqueProjectTags.add("Untagged");
          tagList.push("Untagged");
        }
        continue;
      }

      const tag = capitalizeFirstLetter(rawTag);

      if (!uniqueProjectTags.has(tag)) {
        uniqueProjectTags.add(tag);
        tagList.push(tag);
      }
    }

    setProjectTags(tagList);
    return tagList;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProjectData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleProjectFormEntry = async () => {
    if (!projectData.name.trim()) {
      setErrorMessage("Project Name cannot be empty!");
      return;
    }

    try {
      setErrorMessage("");
      await saveUserProject(projectData);
      resetForm();
      closeModal();
    } catch (error) {
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to save project. Please try again.";

      // console.error("handleProjectFormEntry error:", {
      //   projectData,
      //   backend: error?.response?.data,
      //   message: error.message,
      // });

      setErrorMessage(backendError);
    }
  };

  const resetForm = () => {
    setProjectData({
      name: "",
      description: "",
      priority: "1",
      status: "Not Started",
      dateCreated: "",
      dateModified: "",
      tag: "",
    });
    setErrorMessage("");
  };

  const closeModal = () => {
    setIsModalVisible(false);
    resetForm();
  };

  const sortProjectsByAscendingName = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByDescendingName = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      b.name.toLowerCase().localeCompare(a.name.toLowerCase()),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByDateCreatedOldest = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      (a.dateCreated || "").localeCompare(b.dateCreated || ""),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByDateCreatedNewest = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      (b.dateCreated || "").localeCompare(a.dateCreated || ""),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByDateModifiedOldest = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      (a.dateModified || "").localeCompare(b.dateModified || ""),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByDateModifiedNewest = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) =>
      (b.dateModified || "").localeCompare(a.dateModified || ""),
    );
    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByPriority = () => {
    const sortedProjects = [...filteredProjects].sort((a, b) => {
      const currentProjectPriority = parseInt(a.priority, 10);
      const subsequentProjectPriority = parseInt(b.priority, 10);

      if (currentProjectPriority === subsequentProjectPriority) {
        return (a.dateCreated || "").localeCompare(b.dateCreated || "");
      }

      return subsequentProjectPriority - currentProjectPriority;
    });

    setFilteredProjects(sortedProjects);
  };

  const sortProjectsByProgress = () => {
    const statusOrder = {
      Completed: 0,
      "On Hold": 1,
      "In Progress": 2,
      "Not Started": 3,
    };

    const sortedProjects = [...filteredProjects].sort((a, b) => {
      const currentProjectStatus = statusOrder[a.status] ?? Infinity;
      const subsequentProjectStatus = statusOrder[b.status] ?? Infinity;

      if (currentProjectStatus === subsequentProjectStatus) {
        return (a.dateCreated || "").localeCompare(b.dateCreated || "");
      }

      return currentProjectStatus - subsequentProjectStatus;
    });

    setFilteredProjects(sortedProjects);
  };

  const handleSortOptionSelect = async (option) => {
    setSortOption(option);
    setSortDropdownOpen(false);

    switch (option) {
      case "Sorted: By Ascending Name":
        sortProjectsByAscendingName();
        break;
      case "Sorted: By Descending Name":
        sortProjectsByDescendingName();
        break;
      case "Sorted: Date Created (Oldest)":
        sortProjectsByDateCreatedOldest();
        break;
      case "Sorted: Date Created (Newest)":
        sortProjectsByDateCreatedNewest();
        break;
      case "Sorted: Date Modified (Oldest)":
        sortProjectsByDateModifiedOldest();
        break;
      case "Sorted: Date Modified (Newest)":
        sortProjectsByDateModifiedNewest();
        break;
      case "Sorted: Priority":
        sortProjectsByPriority();
        break;
      case "Sorted: Progress":
        sortProjectsByProgress();
        break;
      default:
        setSortOption("Sort By");
    }
  };

  const setSelectedTag = (tag) => {
    setSortOption("Sort By");
    setSelectedMenuTag(tag);
    applyTagFilter(tag, projectsWithAccess);
  };

  const setArchiveTag = async (project, tag) => {
    try {
      await updateSelectedProjectTag(project.id, tag);
    } catch (error) {
      console.error(
        "setArchiveTag error:",
        error?.response?.data || error.message,
      );
    }
  };

  const undoArchiveTag = async (project, tag) => {
    try {
      setSelectedMenuTag("All");
      await updateSelectedProjectTag(project.id, tag);
    } catch (error) {
      console.error(
        "undoArchiveTag error:",
        error?.response?.data || error.message,
      );
    }
  };

  const handleNewProjectClick = async () => {
    try {
      const numProjects = await countUserProjects();

      // console.log("==== NEW PROJECT CLICK ====");
      // console.log("numProjects:", numProjects);
      // console.log("editableProjectLimit:", editableProjectLimit);

      if (numProjects >= editableProjectLimit) {
        setUpgradeModalTitle("Project Limit Reached");
        setUpgradeModalMessage(
          `Your ${capitalizeFirstLetter(
            normalizedUserPlan,
          )} plan allows up to ${editableProjectLimit} editable project${
            editableProjectLimit === 1 ? "" : "s"
          }. Additional projects are view-only. Upgrade your account to unlock editing for more projects.`,
        );
        setIsUpgradeModalOpen(true);
        return;
      }

      setIsFormEditMode(true);
      setIsModalVisible(true);
    } catch (error) {
      console.error(
        "handleNewProjectClick error:",
        error?.response?.data || error.message,
      );
    }
  };

  if (!projectTags || projectTags.length === 0) {
    return <div>No tags available</div>;
  }

  return (
    <div className="project-dashboard-content-container">
      <div className="project-dashboard-header">
        <h2> My Workspace </h2>
        <h3> Project List </h3>
      </div>

      <div className="project-dashboard-menu">
        <div className="left-side">
          <div className="project-dashboard-filter-label-container">
            <div className="project-dashboard-filter-label">
              Filter By Label:
            </div>
            <div className="tag-strip">
              {projectTags.map((tag) => (
                <div
                  key={tag}
                  className={`tag-chip ${
                    selectedMenuTag === tag ? "active" : ""
                  }`}
                  onClick={() => setSelectedTag(tag)}
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="right-side">
          <div className="project-sort-dropdown" ref={dropdownRef}>
            <button
              className="project-dashboard-menu-action-button project-sort"
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
            >
              {sortOption}
            </button>

            {sortDropdownOpen && (
              <div className="project-sort-dropdown-menu">
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Ascending Name")
                  }
                >
                  Name (A-Z)
                </div>
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Descending Name")
                  }
                >
                  Name (Z-A)
                </div>
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Oldest)")
                  }
                >
                  Date Created (Oldest)
                </div>
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Newest)")
                  }
                >
                  Date Created (Newest)
                </div>
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Oldest)")
                  }
                >
                  Date Modified (Oldest)
                </div>
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Newest)")
                  }
                >
                  Date Modified (Newest)
                </div>
                <div onClick={() => handleSortOptionSelect("Sorted: Priority")}>
                  Priority
                </div>
                <div onClick={() => handleSortOptionSelect("Sorted: Progress")}>
                  Progress
                </div>
              </div>
            )}
          </div>

          <button
            className="project-dashboard-menu-action-button project-new"
            onClick={handleNewProjectClick}
          >
            New Project
          </button>
        </div>
      </div>

      <ProjectModalForm
        isVisible={isModalVisible}
        isFormEditMode={isFormEditMode}
        setIsFormEditMode={setIsFormEditMode}
        onClose={closeModal}
        projectData={projectData}
        setProjectData={setProjectData}
        handleInputChange={handleInputChange}
        handleProjectFormEntry={handleProjectFormEntry}
        errorMessage={errorMessage}
      />

      {isUpgradeModalOpen && (
        <div className="upgrade-modal-overlay">
          <div className="upgrade-modal">
            <h3>{upgradeModalTitle}</h3>
            <p>{upgradeModalMessage}</p>

            <div className="upgrade-modal-actions">
              <button onClick={() => setIsUpgradeModalOpen(false)}>
                Cancel
              </button>

              <button
                onClick={() => {
                  setIsUpgradeModalOpen(false);
                  navigate("/billing");
                }}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}

      <ProjectListFeed
        projectsWithAccess={projectsWithAccess}
        filteredProjects={filteredProjects}
        setFilteredProjects={setFilteredProjects}
        selectedMenuTag={selectedMenuTag}
        setSelectedTag={setSelectedTag}
        setArchiveTag={setArchiveTag}
        undoArchiveTag={undoArchiveTag}
        setProjectData={setProjectData}
        setIsModalVisible={setIsModalVisible}
        setIsFormEditMode={setIsFormEditMode}
        capitalizeFirstLetter={capitalizeFirstLetter}
        setIsUpgradeModalOpen={setIsUpgradeModalOpen}
        setUpgradeModalTitle={setUpgradeModalTitle}
        setUpgradeModalMessage={setUpgradeModalMessage}
      />
    </div>
  );
};

export default ProjectDashboardContent;
